/**
 * CreateResearchScopeWizard Component
 * 
 * Modern 4-step wizard for creating a new Research Scope:
 * 
 * Step 1: Name & Description
 * Step 2: Instructions - AI generated or templates with editable fields
 * Step 3: Share with Users & Groups
 * Step 4: Upload Documents
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { 
  Modal,
  Spinner,
  SpinnerSize,
  Toggle
} from '@fluentui/react';
import {
  DocumentRegular,
  ArrowRightFilled,
  ArrowLeftFilled,
  SparkleRegular,
  CheckmarkCircleFilled,
  DeleteRegular,
  DismissRegular,
  BookSearchRegular,
  CloudArrowUpRegular,
  DocumentTextRegular,
  BriefcaseRegular,
  ScalesRegular,
  BeakerRegular,
  CodeRegular,
  PeopleRegular,
  PersonRegular,
  GlobeRegular,
  ArrowSyncRegular
} from '@fluentui/react-icons';
import { 
  createResearchScope, 
  updateResearchScope,
  uploadDocument, 
  generateInstructions,
  enhanceDescription,
  getResearchScopeStatus,
  ResearchScopeDetail,
  ScopeFile,
  ScopeStatus
} from '../../api/researchScopeApi';
import { EntraUserPicker, EntraGroupPicker } from '../EntraUserPicker';

interface CreateResearchScopeWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (scope: ResearchScopeDetail) => void;
}

type WizardStep = 1 | 2 | 3 | 4 | 5;

// Template options with icons
const TEMPLATES = [
  { key: 'custom', label: 'Custom', icon: DocumentTextRegular, description: 'Write your own instructions' },
  { key: 'financial', label: 'Financial', icon: BriefcaseRegular, description: 'Financial reports & analysis' },
  { key: 'legal', label: 'Legal', icon: ScalesRegular, description: 'Contracts & legal documents' },
  { key: 'technical', label: 'Technical', icon: CodeRegular, description: 'Technical documentation' },
  { key: 'research', label: 'Research', icon: BeakerRegular, description: 'Academic & scientific papers' }
];

const TEMPLATE_INSTRUCTIONS: Record<string, { retrieval: string; answer: string }> = {
  financial: {
    retrieval: `Focus on finding:
• Financial statements and metrics
• Revenue, profit, and expense data
• Market analysis and forecasts
• Risk assessments and compliance documents
• Investment and performance data`,
    answer: `Provide answers with:
• Precise numerical data with sources
• Clear financial terminology
• Comparative analysis when relevant
• Risk considerations
• Format numbers professionally`
  },
  legal: {
    retrieval: `Focus on finding:
• Contract terms and clauses
• Legal obligations and rights
• Compliance requirements
• Case references and precedents
• Regulatory requirements`,
    answer: `Provide answers with:
• Precise legal language
• Relevant clause citations
• Potential implications
• Required actions
• Clear structure`
  },
  technical: {
    retrieval: `Focus on finding:
• Technical specifications
• Architecture descriptions
• API documentation
• Implementation details
• Troubleshooting guides`,
    answer: `Provide answers with:
• Clear technical explanations
• Code examples when relevant
• Step-by-step procedures
• Best practices
• Version-specific information`
  },
  research: {
    retrieval: `Focus on finding:
• Research findings and conclusions
• Methodology descriptions
• Data and statistics
• Literature references
• Experimental results`,
    answer: `Provide answers with:
• Evidence-based conclusions
• Proper citations
• Methodology context
• Limitations acknowledgment
• Clear academic format`
  }
};

const SUPPORTED_EXTENSIONS = [
  '.pdf', '.docx', '.doc', '.xlsx', '.xls', 
  '.pptx', '.ppt', '.txt', '.png', '.jpg', '.jpeg', '.gif', '.webp'
];

export const CreateResearchScopeWizard: React.FC<CreateResearchScopeWizardProps> = ({
  isOpen,
  onClose,
  onCreated
}) => {
  const [step, setStep] = useState<WizardStep>(1);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateKey, setTemplateKey] = useState('custom');
  const [retrievalInstructions, setRetrievalInstructions] = useState('');
  const [answerInstructions, setAnswerInstructions] = useState('');
  const [isGeneratingInstructions, setIsGeneratingInstructions] = useState(false);
  const [isEnhancingDescription, setIsEnhancingDescription] = useState(false);
  
  // Sharing state
  const [sharedWithUsers, setSharedWithUsers] = useState<string[]>([]);
  const [sharedWithGroups, setSharedWithGroups] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(false);
  
  // File state
  const [files, setFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<ScopeFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // General state
  const [createdScope, setCreatedScope] = useState<ResearchScopeDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  // Indexing status state (for success step)
  const [indexingStatus, setIndexingStatus] = useState<ScopeStatus | null>(null);
  const [isCheckingStatus, setIsCheckingStatus] = useState(false);
  const statusPollingRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Poll for indexing status when on step 5
  useEffect(() => {
    if (step !== 5 || !createdScope || uploadedFiles.length === 0) {
      // Clear polling if not on success step or no files uploaded
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
      return;
    }

    // Initial status check
    const checkStatus = async () => {
      setIsCheckingStatus(true);
      try {
        const status = await getResearchScopeStatus(createdScope.id);
        setIndexingStatus(status);
        
        // Stop polling if status is ready or error
        if (status.status === 'ready' || status.status === 'error') {
          if (statusPollingRef.current) {
            clearInterval(statusPollingRef.current);
            statusPollingRef.current = null;
          }
        }
      } catch (err) {
        console.error('Failed to check indexing status:', err);
      } finally {
        setIsCheckingStatus(false);
      }
    };

    // Run immediately
    checkStatus();

    // Start polling every 3 seconds
    statusPollingRef.current = setInterval(checkStatus, 3000);

    return () => {
      if (statusPollingRef.current) {
        clearInterval(statusPollingRef.current);
        statusPollingRef.current = null;
      }
    };
  }, [step, createdScope, uploadedFiles.length]);

  const handleClose = useCallback(() => {
    // Clear status polling
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
    setStep(1);
    setName('');
    setDescription('');
    setTemplateKey('custom');
    setRetrievalInstructions('');
    setAnswerInstructions('');
    setSharedWithUsers([]);
    setSharedWithGroups([]);
    setIsPublic(false);
    setFiles([]);
    setUploadedFiles([]);
    setCreatedScope(null);
    setError(null);
    setIsCreating(false);
    setIsUploading(false);
    setIndexingStatus(null);
    setIsCheckingStatus(false);
    onClose();
  }, [onClose]);

  const handleCreateScope = async () => {
    if (!name.trim()) {
      setError('Please enter a name for your research scope');
      return;
    }

    setError(null);
    setIsCreating(true);

    try {
      const scope = await createResearchScope({
        name: name.trim(),
        description: description.trim(),
        retrieval_instructions: retrievalInstructions,
        answer_instructions: answerInstructions
      });
      
      setCreatedScope(scope);
      setStep(2); // Go to Instructions step
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create research scope');
    } finally {
      setIsCreating(false);
    }
  };

  const handleGenerateInstructions = async () => {
    if (!createdScope) return;

    setIsGeneratingInstructions(true);
    setError(null);

    try {
      const instructions = await generateInstructions(createdScope.id);
      setRetrievalInstructions(instructions.retrieval_instructions);
      setAnswerInstructions(instructions.answer_instructions);
      setTemplateKey('custom');
    } catch (err) {
      setError('Failed to generate instructions. You can write custom instructions instead.');
    } finally {
      setIsGeneratingInstructions(false);
    }
  };

  const handleTemplateSelect = (key: string) => {
    setTemplateKey(key);
    if (key !== 'custom' && TEMPLATE_INSTRUCTIONS[key]) {
      setRetrievalInstructions(TEMPLATE_INSTRUCTIONS[key].retrieval);
      setAnswerInstructions(TEMPLATE_INSTRUCTIONS[key].answer);
    }
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
  };

  const addFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(file => {
      const ext = '.' + file.name.split('.').pop()?.toLowerCase();
      return SUPPORTED_EXTENSIONS.includes(ext);
    });

    if (validFiles.length < newFiles.length) {
      setError(`Some files were skipped (unsupported format)`);
    }

    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUploadFiles = async () => {
    if (!createdScope || files.length === 0) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);

    const newUploadedFiles: ScopeFile[] = [];
    
    for (let i = 0; i < files.length; i++) {
      try {
        const uploadedFile = await uploadDocument(createdScope.id, files[i]);
        newUploadedFiles.push(uploadedFile);
        setUploadProgress(((i + 1) / files.length) * 100);
      } catch (err) {
        console.error(`Failed to upload ${files[i].name}:`, err);
        setError(`Failed to upload ${files[i].name}`);
      }
    }

    setUploadedFiles(prev => [...prev, ...newUploadedFiles]);
    setFiles([]);
    setIsUploading(false);
  };

  const handleSaveInstructions = async () => {
    if (!createdScope) return;

    // Save instructions to the scope
    setIsCreating(true);
    setError(null);

    try {
      await updateResearchScope(createdScope.id, {
        retrieval_instructions: retrievalInstructions,
        answer_instructions: answerInstructions
      });
    } catch (err) {
      console.error('Failed to save instructions:', err);
      setError('Failed to save instructions. You can update them later from scope settings.');
    } finally {
      setIsCreating(false);
    }
    
    setStep(3);
  };

  const handleSaveSharing = async () => {
    if (!createdScope) return;

    // Only update if there are sharing changes
    if (sharedWithUsers.length > 0 || sharedWithGroups.length > 0 || isPublic) {
      setIsCreating(true);
      setError(null);

      try {
        await updateResearchScope(createdScope.id, {
          shared_with_users: sharedWithUsers,
          shared_with_groups: sharedWithGroups,
          is_public: isPublic
        });
      } catch (err) {
        console.error('Failed to save sharing settings:', err);
        setError('Failed to save sharing settings. You can update them later from scope settings.');
      } finally {
        setIsCreating(false);
      }
    }
    
    setStep(4);
  };

  const handleFinish = () => {
    // Go to success step
    setStep(5);
  };

  const handleFinalClose = () => {
    if (createdScope) {
      onCreated(createdScope);
    }
    handleClose();
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const stepTitles = ['Create Scope', 'Configure Instructions', 'Share Access', 'Add Documents', 'All Done!'];
  const stepDescriptions = [
    'Give your research collection a name and description',
    'Customize how AI searches and answers from your documents',
    'Share this scope with specific users or groups',
    'Upload documents to your research scope',
    'Your research scope is ready to use'
  ];

  return (
    <Modal
      isOpen={isOpen}
      onDismiss={handleClose}
      isBlocking={true}
      styles={{
        main: {
          maxWidth: '720px',
          width: '90vw',
          maxHeight: '90vh',
          borderRadius: '16px',
          overflow: 'hidden',
          boxShadow: '0 24px 80px rgba(0,0,0,0.2)'
        },
        scrollableContent: {
          overflow: 'hidden'
        }
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '85vh', maxHeight: '700px' }}>
        {/* Header */}
        <div style={{
          padding: '28px 32px 24px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          position: 'relative'
        }}>
          <button
            onClick={handleClose}
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.3)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
          >
            <DismissRegular style={{ color: 'white', fontSize: '18px' }} />
          </button>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
            <div style={{
              width: '52px',
              height: '52px',
              borderRadius: '14px',
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <BookSearchRegular style={{ fontSize: '28px' }} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600 }}>
                {stepTitles[step - 1]}
              </h1>
              <p style={{ margin: '4px 0 0', opacity: 0.9, fontSize: '14px' }}>
                {stepDescriptions[step - 1]}
              </p>
            </div>
          </div>

          {/* Step indicators - hide on success step */}
          {step < 5 && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              {[1, 2, 3, 4].map((s) => (
                <div
                  key={s}
                  style={{
                    flex: 1,
                    height: '4px',
                    borderRadius: '2px',
                    background: s <= step ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                    transition: 'background 0.3s'
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            margin: '16px 32px 0',
            padding: '14px 18px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: '10px',
            color: '#dc2626',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <span>{error}</span>
            <button
              onClick={() => setError(null)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#dc2626',
                padding: '4px'
              }}
            >
              <DismissRegular />
            </button>
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '32px' }}>
          {/* Step 1: Name & Description */}
          {step === 1 && (
            <div style={{ maxWidth: '560px', margin: '0 auto' }}>
              <div style={{ marginBottom: '28px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '10px'
                }}>
                  Scope Name <span style={{ color: '#dc2626' }}>*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Q4 Financial Reports"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    outline: 'none',
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 4px rgba(102,126,234,0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '10px'
                }}>
                  Description
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe what documents you'll add and what kind of questions you want to ask. This helps the AI understand your research scope better."
                  rows={5}
                  style={{
                    width: '100%',
                    padding: '16px 20px',
                    fontSize: '15px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: 1.6,
                    transition: 'border-color 0.2s, box-shadow 0.2s',
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 4px rgba(102,126,234,0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginTop: '12px' 
                }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                    A good description helps generate better AI instructions.
                  </p>
                  <button
                    onClick={async () => {
                      if (!name.trim()) {
                        setError('Please enter a scope name first');
                        return;
                      }
                      setIsEnhancingDescription(true);
                      setError(null);
                      try {
                        const enhanced = await enhanceDescription(name, description);
                        setDescription(enhanced);
                      } catch (err) {
                        console.error('Failed to enhance description:', err);
                        setError('Failed to enhance description. Please try again.');
                      } finally {
                        setIsEnhancingDescription(false);
                      }
                    }}
                    disabled={isEnhancingDescription}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: isEnhancingDescription ? 'wait' : 'pointer',
                      opacity: isEnhancingDescription ? 0.7 : 1,
                      transition: 'transform 0.2s, opacity 0.2s',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => !isEnhancingDescription && (e.currentTarget.style.transform = 'translateY(-1px)')}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    {isEnhancingDescription ? (
                      <Spinner size={SpinnerSize.xSmall} />
                    ) : (
                      <SparkleRegular style={{ fontSize: '14px' }} />
                    )}
                    {isEnhancingDescription ? 'Enhancing...' : 'Enhance with AI'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Instructions */}
          {step === 2 && (
            <div style={{ maxWidth: '600px', margin: '0 auto' }}>
              {/* Template selector */}
              <div style={{ marginBottom: '28px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '14px'
                }}>
                  <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                    Choose a Template
                  </label>
                  <button
                    onClick={handleGenerateInstructions}
                    disabled={isGeneratingInstructions || !createdScope}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 18px',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: isGeneratingInstructions ? 'wait' : 'pointer',
                      opacity: isGeneratingInstructions ? 0.7 : 1,
                      transition: 'transform 0.2s, opacity 0.2s'
                    }}
                    onMouseEnter={(e) => !isGeneratingInstructions && (e.currentTarget.style.transform = 'translateY(-1px)')}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    {isGeneratingInstructions ? (
                      <Spinner size={SpinnerSize.xSmall} styles={{ root: { marginRight: 4 } }} />
                    ) : (
                      <SparkleRegular style={{ fontSize: '16px' }} />
                    )}
                    {isGeneratingInstructions ? 'Generating...' : 'Generate with AI'}
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
                  {TEMPLATES.map((template) => {
                    const Icon = template.icon;
                    const isSelected = templateKey === template.key;
                    return (
                      <button
                        key={template.key}
                        onClick={() => handleTemplateSelect(template.key)}
                        style={{
                          padding: '16px 12px',
                          background: isSelected ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#f9fafb',
                          border: isSelected ? 'none' : '2px solid #e5e7eb',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          textAlign: 'center'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#667eea';
                            e.currentTarget.style.background = '#f3f4f6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.borderColor = '#e5e7eb';
                            e.currentTarget.style.background = '#f9fafb';
                          }
                        }}
                      >
                        <Icon style={{
                          fontSize: '22px',
                          color: isSelected ? 'white' : '#6b7280',
                          display: 'block',
                          margin: '0 auto 8px'
                        }} />
                        <span style={{
                          fontSize: '12px',
                          fontWeight: 600,
                          color: isSelected ? 'white' : '#374151'
                        }}>
                          {template.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Instructions fields */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '10px'
                }}>
                  Search Instructions
                </label>
                <textarea
                  value={retrievalInstructions}
                  onChange={(e) => setRetrievalInstructions(e.target.value)}
                  placeholder="How should the AI search through your documents?"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    fontSize: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: 1.5,
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '10px'
                }}>
                  Answer Instructions
                </label>
                <textarea
                  value={answerInstructions}
                  onChange={(e) => setAnswerInstructions(e.target.value)}
                  placeholder="How should the AI format and present answers?"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '14px 18px',
                    fontSize: '14px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    outline: 'none',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    lineHeight: 1.5,
                    boxSizing: 'border-box'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#667eea'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </div>
          )}

          {/* Step 3: Share Access */}
          {step === 3 && (
            <div style={{ maxWidth: '560px', margin: '0 auto' }}>
              {/* Public toggle */}
              <div style={{
                padding: '20px 24px',
                background: isPublic ? 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)' : '#f9fafb',
                border: `2px solid ${isPublic ? '#667eea' : '#e5e7eb'}`,
                borderRadius: '12px',
                marginBottom: '24px',
                transition: 'all 0.2s'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: isPublic ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : '#e5e7eb',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <GlobeRegular style={{ fontSize: '22px', color: isPublic ? 'white' : '#6b7280' }} />
                    </div>
                    <div>
                      <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#374151' }}>
                        Public Access
                      </h4>
                      <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>
                        Anyone in your organization can view this scope
                      </p>
                    </div>
                  </div>
                  <Toggle
                    checked={isPublic}
                    onChange={(_, checked) => setIsPublic(checked || false)}
                    styles={{
                      root: { margin: 0 },
                      pill: { background: isPublic ? '#667eea' : undefined }
                    }}
                  />
                </div>
              </div>

              {/* Share with specific users/groups */}
              {!isPublic && (
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <PersonRegular style={{ fontSize: '18px', color: '#667eea' }} />
                      <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                        Share with Users
                      </label>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: '#f9fafb',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <EntraUserPicker
                        selectedEmails={sharedWithUsers}
                        onSelectionChange={setSharedWithUsers}
                        label=""
                        placeholder="Search users by name or email..."
                        description="These users will have read access to your research scope"
                      />
                    </div>
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                      <PeopleRegular style={{ fontSize: '18px', color: '#667eea' }} />
                      <label style={{ fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                        Share with Groups
                      </label>
                    </div>
                    <div style={{
                      padding: '16px',
                      background: '#f9fafb',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <EntraGroupPicker
                        selectedGroupIds={sharedWithGroups}
                        onSelectionChange={setSharedWithGroups}
                        label=""
                        placeholder="Search groups..."
                        description="All members of these groups will have read access"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Info note */}
              <div style={{
                padding: '14px 18px',
                background: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '10px',
                fontSize: '13px',
                color: '#0369a1'
              }}>
                💡 You can always change sharing settings later from the scope settings.
              </div>
            </div>
          )}

          {/* Step 4: Upload Documents */}
          {step === 4 && (
            <div style={{ maxWidth: '560px', margin: '0 auto' }}>
              {/* Drop zone */}
              <div
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: `2px dashed ${isDragging ? '#667eea' : '#d1d5db'}`,
                  borderRadius: '16px',
                  padding: '48px 32px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isDragging ? 'rgba(102,126,234,0.05)' : '#fafafa',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  if (!isDragging) {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.background = 'rgba(102,126,234,0.02)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDragging) {
                    e.currentTarget.style.borderColor = '#d1d5db';
                    e.currentTarget.style.background = '#fafafa';
                  }
                }}
              >
                <div style={{
                  width: '72px',
                  height: '72px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <CloudArrowUpRegular style={{ fontSize: '36px', color: 'white' }} />
                </div>
                <p style={{ margin: '0 0 8px', fontSize: '17px', fontWeight: 600, color: '#374151' }}>
                  Drop files here or click to browse
                </p>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                  PDF, Word, Excel, PowerPoint, Text, Images
                </p>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={SUPPORTED_EXTENSIONS.join(',')}
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />

              {/* Upload progress */}
              {isUploading && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '10px'
                  }}>
                    <span style={{ fontSize: '14px', fontWeight: 500, color: '#374151' }}>
                      Uploading files...
                    </span>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>
                      {Math.round(uploadProgress)}%
                    </span>
                  </div>
                  <div style={{
                    height: '8px',
                    background: '#e5e7eb',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      width: `${uploadProgress}%`,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      borderRadius: '4px',
                      transition: 'width 0.3s'
                    }} />
                  </div>
                </div>
              )}

              {/* Pending files */}
              {files.length > 0 && !isUploading && (
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
                    Ready to upload ({files.length})
                  </h4>
                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    maxHeight: '180px',
                    overflowY: 'auto'
                  }}>
                    {files.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '14px 16px',
                          borderBottom: index < files.length - 1 ? '1px solid #f3f4f6' : 'none',
                          background: 'white'
                        }}
                      >
                        <DocumentRegular style={{ fontSize: '20px', color: '#6b7280', marginRight: '12px' }} />
                        <span style={{
                          flex: 1,
                          fontSize: '14px',
                          color: '#374151',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}>
                          {file.name}
                        </span>
                        <span style={{ fontSize: '12px', color: '#9ca3af', marginRight: '12px' }}>
                          {formatFileSize(file.size)}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeFile(index); }}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '6px',
                            borderRadius: '6px',
                            color: '#9ca3af',
                            transition: 'color 0.2s, background 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#dc2626';
                            e.currentTarget.style.background = '#fef2f2';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#9ca3af';
                            e.currentTarget.style.background = 'none';
                          }}
                        >
                          <DeleteRegular style={{ fontSize: '16px' }} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Uploaded files */}
              {uploadedFiles.length > 0 && (
                <div style={{ marginTop: '24px' }}>
                  <h4 style={{
                    margin: '0 0 12px',
                    fontSize: '14px',
                    fontWeight: 600,
                    color: '#059669',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <CheckmarkCircleFilled style={{ fontSize: '18px' }} />
                    Uploaded successfully ({uploadedFiles.length})
                  </h4>
                  <div style={{
                    background: '#ecfdf5',
                    border: '1px solid #a7f3d0',
                    borderRadius: '12px',
                    padding: '14px 18px',
                    maxHeight: '120px',
                    overflowY: 'auto'
                  }}>
                    {uploadedFiles.map((file) => (
                      <div key={file.id} style={{
                        fontSize: '14px',
                        color: '#065f46',
                        padding: '4px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <CheckmarkCircleFilled style={{ fontSize: '14px', color: '#10b981' }} />
                        {file.filename}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 5: Success */}
          {step === 5 && (
            <div style={{ maxWidth: '520px', margin: '0 auto', textAlign: 'center', padding: '20px 0' }}>
              {/* Status indicator based on indexing status */}
              {uploadedFiles.length > 0 && (!indexingStatus || indexingStatus.status === 'indexing') ? (
                <>
                  {/* Indexing in progress */}
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 28px',
                    boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}>
                    <ArrowSyncRegular style={{ 
                      fontSize: '48px', 
                      color: 'white',
                      animation: 'spin 2s linear infinite'
                    }} />
                  </div>
                  <style>
                    {`
                      @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                      }
                      @keyframes pulse {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.05); }
                      }
                    `}
                  </style>

                  <h2 style={{
                    margin: '0 0 12px',
                    fontSize: '26px',
                    fontWeight: 700,
                    color: '#1f2937'
                  }}>
                    Indexing Your Documents...
                  </h2>

                  <p style={{
                    margin: '0 0 24px',
                    fontSize: '16px',
                    color: '#6b7280',
                    lineHeight: 1.6
                  }}>
                    Your research collection <strong style={{ color: '#374151' }}>"{createdScope?.name}"</strong> is being prepared.
                    <br />This usually takes 1-2 minutes.
                  </p>

                  {/* Progress indicator */}
                  <div style={{
                    padding: '20px 24px',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    borderRadius: '14px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      marginBottom: '12px'
                    }}>
                      <Spinner size={SpinnerSize.medium} />
                      <span style={{ fontSize: '15px', fontWeight: 600, color: '#1d4ed8' }}>
                        Processing {uploadedFiles.length} document{uploadedFiles.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <p style={{ margin: 0, fontSize: '13px', color: '#3b82f6' }}>
                      The AI is analyzing and indexing your content for fast retrieval
                    </p>
                  </div>

                  <p style={{ margin: 0, fontSize: '13px', color: '#9ca3af' }}>
                    You can close this window - indexing will continue in the background
                  </p>
                </>
              ) : indexingStatus?.status === 'error' ? (
                <>
                  {/* Error state */}
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 28px',
                    boxShadow: '0 8px 32px rgba(245, 158, 11, 0.3)'
                  }}>
                    <span style={{ fontSize: '48px' }}>⚠️</span>
                  </div>

                  <h2 style={{
                    margin: '0 0 12px',
                    fontSize: '26px',
                    fontWeight: 700,
                    color: '#1f2937'
                  }}>
                    Scope Created with Warnings
                  </h2>

                  <p style={{
                    margin: '0 0 32px',
                    fontSize: '16px',
                    color: '#6b7280',
                    lineHeight: 1.6
                  }}>
                    Your research collection was created but some documents may not have indexed correctly.
                    You can try re-uploading them later.
                  </p>
                </>
              ) : (
                <>
                  {/* Success state - ready to query */}
                  <div style={{
                    width: '100px',
                    height: '100px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 28px',
                    boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)'
                  }}>
                    <CheckmarkCircleFilled style={{ fontSize: '52px', color: 'white' }} />
                  </div>

                  <h2 style={{
                    margin: '0 0 12px',
                    fontSize: '26px',
                    fontWeight: 700,
                    color: '#1f2937'
                  }}>
                    {uploadedFiles.length > 0 ? 'Ready to Research!' : 'Research Scope Created!'}
                  </h2>

                  <p style={{
                    margin: '0 0 32px',
                    fontSize: '16px',
                    color: '#6b7280',
                    lineHeight: 1.6
                  }}>
                    Your research collection <strong style={{ color: '#374151' }}>"{createdScope?.name}"</strong> {uploadedFiles.length > 0 ? 'is ready for queries!' : 'has been created successfully.'}
                  </p>

                  {uploadedFiles.length > 0 && (
                    <div style={{
                      padding: '16px 20px',
                      background: '#ecfdf5',
                      border: '1px solid #a7f3d0',
                      borderRadius: '12px',
                      marginBottom: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px'
                    }}>
                      <CheckmarkCircleFilled style={{ fontSize: '20px', color: '#10b981' }} />
                      <span style={{ fontSize: '15px', fontWeight: 500, color: '#065f46' }}>
                        {uploadedFiles.length} document{uploadedFiles.length > 1 ? 's' : ''} indexed and ready
                      </span>
                    </div>
                  )}
                </>
              )}

              {/* Info cards - always show */}
              <div style={{ 
                display: 'grid', 
                gap: '16px',
                textAlign: 'left',
                marginTop: '16px'
              }}>
                <div style={{
                  padding: '18px 22px',
                  background: 'linear-gradient(135deg, rgba(102,126,234,0.08) 0%, rgba(118,75,162,0.08) 100%)',
                  border: '1px solid rgba(102,126,234,0.2)',
                  borderRadius: '14px',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <BookSearchRegular style={{ fontSize: '20px', color: 'white' }} />
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 600, color: '#374151' }}>
                      Start Researching
                    </h4>
                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
                      Find your new scope in the <strong>Data Source</strong> selector. Select it to start asking questions about your documents.
                    </p>
                  </div>
                </div>

                <div style={{
                  padding: '18px 22px',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '14px',
                  display: 'flex',
                  gap: '16px',
                  alignItems: 'flex-start'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <DocumentRegular style={{ fontSize: '20px', color: '#6b7280' }} />
                  </div>
                  <div>
                    <h4 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 600, color: '#374151' }}>
                      Edit Anytime
                    </h4>
                    <p style={{ margin: 0, fontSize: '14px', color: '#6b7280', lineHeight: 1.5 }}>
                      You can edit the scope details, upload more documents, or adjust AI instructions anytime from the <strong>Actions menu</strong> (⋯).
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '20px 32px',
          borderTop: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: step === 5 ? 'center' : 'space-between',
          alignItems: 'center',
          background: '#fafafa'
        }}>
          {step < 5 && (
          <>
          <button
            onClick={step === 1 ? handleClose : () => setStep((step - 1) as WizardStep)}
            disabled={isCreating || isUploading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '14px 24px',
              background: 'white',
              border: '2px solid #e5e7eb',
              borderRadius: '12px',
              fontSize: '15px',
              fontWeight: 600,
              color: '#374151',
              cursor: 'pointer',
              transition: 'border-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#d1d5db'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#e5e7eb'}
          >
            {step > 1 && <ArrowLeftFilled style={{ fontSize: '14px' }} />}
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          <div style={{ display: 'flex', gap: '12px' }}>
            {step === 4 && files.length > 0 && (
              <button
                onClick={handleUploadFiles}
                disabled={isUploading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: '#10b981',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#10b981'}
              >
                {isUploading ? <Spinner size={SpinnerSize.small} /> : <CloudArrowUpRegular style={{ fontSize: '18px' }} />}
                Upload Files
              </button>
            )}

            {step === 1 && (
              <button
                onClick={handleCreateScope}
                disabled={!name.trim() || isCreating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: !name.trim() || isCreating ? '#9ca3af' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  cursor: !name.trim() || isCreating ? 'not-allowed' : 'pointer',
                  transition: 'transform 0.2s, opacity 0.2s'
                }}
                onMouseEnter={(e) => !isCreating && name.trim() && (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {isCreating ? <Spinner size={SpinnerSize.small} /> : null}
                {isCreating ? 'Creating...' : 'Create Scope'}
                {!isCreating && <ArrowRightFilled style={{ fontSize: '14px' }} />}
              </button>
            )}

            {step === 2 && (
              <button
                onClick={handleSaveInstructions}
                disabled={isCreating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  cursor: isCreating ? 'wait' : 'pointer',
                  opacity: isCreating ? 0.7 : 1,
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => !isCreating && (e.currentTarget.style.transform = 'translateY(-1px)')}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                {isCreating ? <Spinner size={SpinnerSize.small} /> : null}
                {isCreating ? 'Saving...' : 'Save & Continue'}
                {!isCreating && <ArrowRightFilled style={{ fontSize: '14px' }} />}
              </button>
            )}

            {step === 3 && (
              <button
                onClick={handleSaveSharing}
                disabled={isCreating}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Continue
                <ArrowRightFilled style={{ fontSize: '14px' }} />
              </button>
            )}

            {step === 4 && (
              <button
                onClick={handleFinish}
                disabled={isUploading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '14px 28px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: 600,
                  color: 'white',
                  cursor: 'pointer',
                  transition: 'transform 0.2s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <CheckmarkCircleFilled style={{ fontSize: '16px' }} />
                {uploadedFiles.length > 0 ? 'Finish' : 'Skip & Finish'}
              </button>
            )}
          </div>
          </>
          )}

          {/* Success step - button changes based on indexing status */}
          {step === 5 && (
            <>
              {/* Show "Close" during indexing, "Start Researching" when ready */}
              {uploadedFiles.length > 0 && (!indexingStatus || indexingStatus.status === 'indexing') ? (
                <button
                  onClick={handleFinalClose}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '16px 40px',
                    background: 'white',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#374151',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.color = '#667eea';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.color = '#374151';
                  }}
                >
                  <DismissRegular style={{ fontSize: '18px' }} />
                  Close & Continue in Background
                </button>
              ) : (
                <button
                  onClick={handleFinalClose}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '16px 40px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: 'white',
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    boxShadow: '0 4px 16px rgba(102,126,234,0.3)'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                >
                  <BookSearchRegular style={{ fontSize: '20px' }} />
                  Start Researching
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreateResearchScopeWizard;
